/**
 * Table  component props
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BP, isSchemaConstArrayModel, QUERY_FN_OP, queryModelParents, SCHEMA_VALUE_TYPE } from "@adapptio/lib-blueprint";

import {
	SORT_ORDER_MODE,
	SORT_ORDER_MODE_default,
	BackgroundColorSchema,
	ForegroundColorSchema,
	termsEditor as LibComponentsTerms,
	getValuesFromStringEnum,
	propGroups,
	TextValueSchema,
	NameSchema
} from "@adapptio/lib-components";

import { isFunction } from "@adapptio/lib-shared";

import { termsEditor } from "../../terms";
import { TableBaseProps, TableColumnBaseProps } from "./baseProps";

export const ComponentTable_Props = {
	...TableBaseProps,

	// Has to be Const.Array because of scoped template or something
	columns: BP.Prop(
		BP.Const.Array({
			...termsEditor.schemas.table.columns,
			/*
			@todo David & Jirka; doesn't work, why?

			default: [{
			titles: [],
			horizontalAlign: TABLE_DEFAULT_HORIZONTAL_ALIGN,
			content: [],
			width: "",
			minWidth: "",
			visible: true,
			render: true
		}],*/
			fallbackValue: [],
			constraints: {
				required: true
			},
			items: BP.Const.Object({
				alias: "TableColumn",
				props: {
					...TableColumnBaseProps,

					titles: BP.Prop(
						BP.Array({
							...termsEditor.schemas.table.columnTitles,
							default: [],
							fallbackValue: [],
							items: BP.Object({
								props: {
									textValue: BP.Prop(
										TextValueSchema({
											alias: "textValue_tableTitle",
											...termsEditor.schemas.table.columnTitle,
											editorOptions: {
												layoutType: ""
											}
										}),
										0
									),

									sortKey: BP.Prop(
										BP.String({
											...termsEditor.schemas.table.columnSortKey
										}),
										10
									),

									sortOrderMode: BP.Prop(
										BP.Enum.String({
											...termsEditor.schemas.table.columnSortOrderMode,
											options: getValuesFromStringEnum(
												SORT_ORDER_MODE,
												LibComponentsTerms.schemas.common.sortOrderModeValues
											),
											default: SORT_ORDER_MODE_default
										}),
										20
									)
								},
								editorOptions: {
									layoutType: "passthrough"
								}
							}),
							constraints: {
								required: true
							},
							outlineOptions: {
								displayChildren: true,
								allowAddElement: true
							},
							getElementModelNodeInfo: (modelNode) => {
								return {
									label:
										modelNode.type === SCHEMA_VALUE_TYPE.CONST &&
										modelNode.constant.props.textValue.type === SCHEMA_VALUE_TYPE.CONST &&
										modelNode.constant.props.textValue.constant.value,
									icon: "mdi/format-title"
								};
							}
						}),
						10
					),

					subheaderContent: BP.Prop(
						BP.ComponentList({
							...termsEditor.schemas.table.columnSubheaderContent,
							getModelNodeInfo: (modelNode) => {
								const tableColumn = queryModelParents(modelNode, (parent) => parent.schema.opts.alias === "TableColumn"
									? { match: true, op: QUERY_FN_OP.BREAK }
									: { match: false, op: QUERY_FN_OP.CONTINUE }
								)[0];

								const columnsArray = isSchemaConstArrayModel(tableColumn?.parent)
									? tableColumn?.parent
									: null;

								const tableColumnIndex = columnsArray
									? columnsArray.items.findIndex((item) => item === tableColumn)
									: -1;

								return {
									label: `Subheader #${tableColumnIndex >= 0 ? tableColumnIndex + 1 : "Subheader"}`,
									icon: "mdi/selection"
								}
							}
						}),
						80
					),

					content: BP.Prop(
						BP.ScopedTemplate({
							...termsEditor.schemas.table.columnContent,
							template: BP.ComponentList({
								...termsEditor.schemas.table.columnContent,
								getModelNodeInfo: (modelNode) => {
									const tableColumn = queryModelParents(modelNode, (parent) => parent.schema.opts.alias === "TableColumn"
										? { match: true, op: QUERY_FN_OP.BREAK }
										: { match: false, op: QUERY_FN_OP.CONTINUE }
									)[0];

									const columnsArray = isSchemaConstArrayModel(tableColumn?.parent)
										? tableColumn?.parent
										: null;

									const tableColumnIndex = columnsArray
										? columnsArray.items.findIndex((item) => item === tableColumn)
										: -1;

									return {
										label: `Column #${tableColumnIndex >= 0 ? tableColumnIndex + 1 : "Column"}`,
										icon: "mdi/table-column"
									}
								}
							})
						}),
						90
					)
				},
				outlineOptions: {
					displayChildren: true,
					displayOnlyProps: [ "titles", "content" ]
				}
			}),
			outlineOptions: {
				displayChildren: true,
				allowAddElement: true
			},
			getElementModelNodeInfo: (modelNode) => {
				if (modelNode.props.titles.type === SCHEMA_VALUE_TYPE.CONST) {
					const { getElementModelNodeInfo } = modelNode.props.titles.constant.schema.opts;

					if (isFunction(getElementModelNodeInfo)) {
						return {
							label: modelNode.props.titles.constant.items
								.map((item, itemIndex) => {
									return (
										item.type === SCHEMA_VALUE_TYPE.CONST &&
										getElementModelNodeInfo(item, itemIndex)?.label
									);
								})
								.filter(Boolean)
								.join(", "),
							icon: "mdi/table-columns"
						};
					}
				}
			}
		}),
		0,
		propGroups.common
	),

	showSubheaderRow: BP.Prop(
		BP.Boolean({
			...termsEditor.schemas.table.showSubheaderRow,
			default: false,
			fallbackValue: false
		}),
		90,
		propGroups.common
	),

	showLoading: BP.Prop(
		BP.Boolean({
			...termsEditor.schemas.table.showLoading,
			default: true,
			fallbackValue: false
		}),
		100,
		propGroups.common
	),

	rowOptions: BP.Prop(
		BP.ScopedTemplate({
			...termsEditor.schemas.table.rowOptions,
			template: BP.Object({
				props: {
					key: BP.Prop(
						BP.String({
							...termsEditor.schemas.table.rowOptionsKey,
							default: null
						}),
						0
					),

					highlight: BP.Prop(
						BP.Array({
							...termsEditor.schemas.table.rowOptionsHighlight,
							items: BP.Object({
								props: {
									name: BP.Prop(NameSchema(), 0),

									condition: BP.Prop(
										BP.Boolean({
											...termsEditor.schemas.table.rowOptionsHighlightCondition,
											default: false,
											fallbackValue: false
										}),
										10
									),

									backgroundColor: BP.Prop(BackgroundColorSchema(), 20),

									foregroundColor: BP.Prop(ForegroundColorSchema(), 30)
								},
								editorOptions: {
									layoutType: "noHeader"
								}
							}),
							getElementModelNodeInfo: (modelNode) => {
								return {
									label:
										modelNode.type === SCHEMA_VALUE_TYPE.CONST &&
										modelNode.constant.props.name.type === SCHEMA_VALUE_TYPE.CONST &&
										modelNode.constant.props.name.constant.value,
									icon: "mdi/tune"
								};
							}
						}),
						10
					)
				}
			}),
			editorOptions: {
				layoutType: "noHeader"
			}
		}),
		0,
		{
			...termsEditor.schemas.table.rowOptions,
			id: "rowOptions",
			order: propGroups.common.order + 1,
			defaultExpanded: true
		}
	)
};
